import Logger from './Logger';
import { attachMessageListener, getDeviceInfo, getLocationQueryParam, setToken, checkExtension } from './Upshow';
import axios from 'axios';
import { delay } from 'bluebird';
import StateService from './services/StateService';

export function getWebviewName () {
    const url = new URL(window.location.href);

    //Hash is the highest priority webview name
    if (url.hash) {
        return { name: url.hash.replace('#', '').toLowerCase(), type: 'hash' };
    }

    //Second is ?name parameter
    if (url.searchParams.has('name')) {
        return { name: url.searchParams.get('name'), type: 'params' };
    }

    //Finally, if we didn't find a webview name, we'll use the subdomain if present, else we return undefined
    const match = new RegExp(/([\w-+]+)\.(localhost|upshowapp\.com|ui6\.internal\.upshow\.tv|ui6\.upshow\.tv)$/).exec(url.hostname);
    if (match) {
        return { name: match[1], type: 'subdomain' };
    }

    return null;
}

function getAccessToken () {
    // I got a Token in the query string
    const queryParamAccessToken = getLocationQueryParam('api_token');
    if (queryParamAccessToken) return queryParamAccessToken;

    // I have a Token saved in Local Storage
    const localStorageAccessToken = window.localStorage.getItem('access-token');
    if (localStorageAccessToken) return localStorageAccessToken;

    return null;
}

async function activationHelper (path) {
    let headers = {};

    const device_info = getDeviceInfo();
    if (device_info.serial) headers['x-serial'] = device_info.serial;

    return await axios.get(process.env.REACT_APP_ACTIVATION_URL + path, { headers })
        .then(results => results.data ?? {})
        .catch(error => {
            Logger.error(['activation'], error, { path });
            throw error;
        });
}

export function cleanupStoredKeys () {
    window.localStorage.removeItem('access-token');
    window.localStorage.removeItem('activation-code');
}

const displayChecks = () => {
    const div = document.createElement('div');
    const iframe = document.createElement('iframe');
    const checksContainerRef = document.getElementById('checksContainer');
    
    if(!checksContainerRef) {
        iframe.setAttribute('src', 'https://checks.upshow.tv/');
        iframe.setAttribute('id', 'checksIframe');
        div.append(iframe);
        div.setAttribute('id', 'checksContainer');
        document.body.appendChild(div);
        
        setTimeout(() => {
            const checksContainerRef = document.getElementById('checksContainer');
            checksContainerRef.remove();
        }, 20000)
    }
}

class Activation {
    webviewName = null;
    accessToken = null;
    activationCode = null;

    constructor () {
        this.start = this.start.bind(this);
        this.cleanup = this.cleanup.bind(this);
        this.askForActivation = this.askForActivation.bind(this);
        this.autoActivate = this.autoActivate.bind(this);
        this.activation = this.activation.bind(this);
    }

    async waitForChromeOSExtension (DeviceUptime) {
        let first = 'Device is initializing... please standby.';
        let second = '';

        if (DeviceUptime > 10000) {
            first = 'Device encountered an issue during initialization. Please restart.';
            second = 'If issues persist, check for pending updates or contact support.';
        }

        let div = document.createElement('div');
        div.innerHTML = `<div class="logo">
                    <img alt="logo" src='https://cdn.upshow.tv/ui-assets/EP_PBU_Logo.png'/>
                </div>
                <div class="first">${first}</div>
                <div class="second">${second}</div>`;
        div.id = 'waiting';
        document.body.insertBefore(div, document.getElementById('root'));
        await delay(3000);
        checkExtension();
        return this.start();
    }

    start () {
        document.getElementById('waiting')?.remove();

        // Do I have a webviewname on the url?
        this.webviewName = getWebviewName();
        if (this.webviewName) {
            return this.autoActivate();
        }

        // Maybe I'm already activated
        this.accessToken = getAccessToken();
        if (this.accessToken) {
            setToken(this.accessToken);

            return this.accessToken;
        }

        // If we are in a PWA and the extension is not loaded yet, we wait for it
        const { extensionAvailable, model, DeviceUptime } = getDeviceInfo();
        if (model === 'ChromeOS PWA') {
            if (!extensionAvailable) {
                return this.waitForChromeOSExtension(DeviceUptime);
            }
        }

        return this.askForActivation();
    }

    cleanup () {
        this.accessToken = null;
        cleanupStoredKeys();
    }

    async askForActivation () {
        let code = localStorage.getItem('activation-code');

        if (!code) {
            const activationCodeResult = await activationHelper('/code')
                .catch(() => {
                    return {};
                });

            code = activationCodeResult.code;
            if (!code) {
                await delay(10000); // Add some delay before trying again
                return this.start();
            } else {
                localStorage.setItem('activation-code', code);
            }
        }

        let div = document.createElement('div');
        div.innerHTML = `<div class="instructions">This is your activation code</div>
                <div class="code">${code}</div>
                <div class="contact">
                    <div>Please reach out to EverPass Support to activate your device!</div>
                    <div>Email support@everpass.com</div>
                    <div>or Call 855-787-7469</div>
                </div>
                <div class="logo">
                    <img alt="logo" src='https://cdn.upshow.tv/ui-assets/EP_PBU_Logo.png'/>
                </div>
                <button id="checks-button" class="hidden">
                    <svg width="75" height="75" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_5_20884)">
                        <path d="M19.6977 12.98C19.7377 12.66 19.7677 12.34 19.7677 12C19.7677 11.66 19.7377 11.34 19.6977 11.02L21.8077 9.37C21.9977 9.22 22.0477 8.95 21.9277 8.73L19.9277 5.27C19.8377 5.11 19.6677 5.02 19.4877 5.02C19.4277 5.02 19.3677 5.03 19.3177 5.05L16.8277 6.05C16.3077 5.65 15.7477 5.32 15.1377 5.07L14.7577 2.42C14.7277 2.18 14.5177 2 14.2677 2H10.2677C10.0177 2 9.80766 2.18 9.77766 2.42L9.39766 5.07C8.78766 5.32 8.22766 5.66 7.70766 6.05L5.21766 5.05C5.15766 5.03 5.09766 5.02 5.03766 5.02C4.86766 5.02 4.69766 5.11 4.60766 5.27L2.60766 8.73C2.47766 8.95 2.53766 9.22 2.72766 9.37L4.83766 11.02C4.79766 11.34 4.76766 11.67 4.76766 12C4.76766 12.33 4.79766 12.66 4.83766 12.98L2.72766 14.63C2.53766 14.78 2.48766 15.05 2.60766 15.27L4.60766 18.73C4.69766 18.89 4.86766 18.98 5.04766 18.98C5.10766 18.98 5.16766 18.97 5.21766 18.95L7.70766 17.95C8.22766 18.35 8.78766 18.68 9.39766 18.93L9.77766 21.58C9.80766 21.82 10.0177 22 10.2677 22H14.2677C14.5177 22 14.7277 21.82 14.7577 21.58L15.1377 18.93C15.7477 18.68 16.3077 18.34 16.8277 17.95L19.3177 18.95C19.3777 18.97 19.4377 18.98 19.4977 18.98C19.6677 18.98 19.8377 18.89 19.9277 18.73L21.9277 15.27C22.0477 15.05 21.9977 14.78 21.8077 14.63L19.6977 12.98ZM17.7177 11.27C17.7577 11.58 17.7677 11.79 17.7677 12C17.7677 12.21 17.7477 12.43 17.7177 12.73L17.5777 13.86L18.4677 14.56L19.5477 15.4L18.8477 16.61L17.5777 16.1L16.5377 15.68L15.6377 16.36C15.2077 16.68 14.7977 16.92 14.3877 17.09L13.3277 17.52L13.1677 18.65L12.9677 20H11.5677L11.3777 18.65L11.2177 17.52L10.1577 17.09C9.72766 16.91 9.32766 16.68 8.92766 16.38L8.01766 15.68L6.95766 16.11L5.68766 16.62L4.98766 15.41L6.06766 14.57L6.95766 13.87L6.81766 12.74C6.78766 12.43 6.76766 12.2 6.76766 12C6.76766 11.8 6.78766 11.57 6.81766 11.27L6.95766 10.14L6.06766 9.44L4.98766 8.6L5.68766 7.39L6.95766 7.9L7.99766 8.32L8.89766 7.64C9.32766 7.32 9.73766 7.08 10.1477 6.91L11.2077 6.48L11.3677 5.35L11.5677 4H12.9577L13.1477 5.35L13.3077 6.48L14.3677 6.91C14.7977 7.09 15.1977 7.32 15.5977 7.62L16.5077 8.32L17.5677 7.89L18.8377 7.38L19.5377 8.59L18.4677 9.44L17.5777 10.14L17.7177 11.27ZM12.2677 8C10.0577 8 8.26766 9.79 8.26766 12C8.26766 14.21 10.0577 16 12.2677 16C14.4777 16 16.2677 14.21 16.2677 12C16.2677 9.79 14.4777 8 12.2677 8ZM12.2677 14C11.1677 14 10.2677 13.1 10.2677 12C10.2677 10.9 11.1677 10 12.2677 10C13.3677 10 14.2677 10.9 14.2677 12C14.2677 13.1 13.3677 14 12.2677 14Z" fill="#222222"/>
                        </g>
                        <defs>
                        <clipPath id="clip0_5_20884">
                        <rect width="24" height="24" fill="white" transform="translate(0.267578)"/>
                        </clipPath>
                        </defs>
                    </svg>
                </button>`;
        div.className = 'Activation';
        document.body.insertBefore(div, document.getElementById('root'));
        setTimeout(() => {
            const checksButtonRef = document.getElementById('checks-button')
            if (['AndroidSkykit','Skykit'].includes(UP.getDeviceInfo().model) && checksButtonRef) {
                checksButtonRef.onclick = displayChecks;
                checksButtonRef.classList.remove("hidden")
            };
        }, 30)
        this.activationCode = code;
        attachMessageListener();

        const api_key = await this.activation();

        document.body.removeChild(div);

        return api_key;
    }

    async activation () {
        let nextActivationAttempt = 2000;
        let keepTrying = true;

        while (keepTrying) {
            // If for some reason an access-token appears (extension probably)
            const localApiKey = window.localStorage.getItem('access-token');
            if (localApiKey) {
                this.accessToken = localApiKey;
                return localApiKey;
            }

            const activationResult = await activationHelper('/' + this.activationCode + '/activate')
                .then((result) => {
                    nextActivationAttempt = 2000;
                    return result;
                })
                .catch((error) => {
                    if (error.response.status === 432) {
                        StateService.showInactiveState();
                        StateService.stop();
                        return { inactive: true };
                    }

                    nextActivationAttempt = nextActivationAttempt * 2;
                    if (nextActivationAttempt > 60000) throw new Error('Activation is failing');
                    return {};
                });

            if (activationResult.inactive === true) {
                keepTrying = false;
            } else if (activationResult.active === true) {
                window.localStorage.setItem('access-token', activationResult.token);
                this.accessToken = activationResult.token;

                return activationResult.token;
            } else if (activationResult.expired === true) {
                this.cleanup();
                document.location.reload();
                keepTrying = false;
            }

            await delay(nextActivationAttempt); //Try again after this delay
        }
    }

    async autoActivate () {
        let path = `/webview/${encodeURI(this.webviewName.name)}/activate`;
        if (this.webviewName.type === 'subdomain') {
            path = path + '?type=pwa';
        }

        const activationResult = await activationHelper(path)
            .catch(error => {
                if (error.response.status === 432) {
                    StateService.showInactiveState();
                    StateService.stop();
                    return { inactive: true };
                } else if (error.response.status === 404) {
                    let url = new URL(window.location);
                    let params = url.searchParams;
                    let origin = url.origin;

                    switch (this.webviewName.type) {
                        case 'hash':
                            break;
                        case 'params':
                            params.delete('name');
                            break;
                        case 'subdomain':
                            origin = url.origin.replace(new RegExp('/' + this.webviewName.name + '\.'), '/');
                            break;
                        default:
                            throw new Error(`Invalid webviewName type ${this.webviewName.type}`);
                    }

                    window.location = origin + '?' + params.toString();

                    return {};
                }
                return {};
            });

        if (activationResult.inactive) {
            return;
        } else if (activationResult.active) {
            this.accessToken = activationResult.token;
            window.localStorage.setItem('access-token', this.accessToken);
            return this.accessToken;
        } else {
            await delay(1000);
            return this.start();
        }
    }
}

export default Activation;
